import { fetchJson, fetchPublicJson, useFetchApi } from '@/api/useFetchApi'
import { parsePreserveJson } from '@/utils/parsePreserveJson'
import { mapApiTripItems } from '@/utils/tripItemMapper'
import { normalizePolicy as normalizePolicyFields } from '@/utils/clientHelpers'
import type {
  CallbackFormModel,
  ChangePolicyDatesPricingRequest,
  ChangePolicyDatesPricingResponse,
  ChangePolicyDatesRequest,
  ClientsDetails,
  DashboardCreditCard,
  GetAgentResponse,
  GetPoliciesResponse,
  GetPolicyDocumentsResponse,
  GetPolicyResponse,
  GetQuotesResponse,
  Policy,
  PolicyTraveler,
  QuoteDocuments,
  TripItem,
} from '@/types/portal'

var onlineApiBase = '/online-api/'

export async function getClientInfoAsync() {
  return fetchJson<ClientsDetails>(`${onlineApiBase}Client-info`)
}

export async function getClientTripsAsync(
  showUpcoming?: boolean | null,
  showExpired?: boolean | null,
  skip = 0,
  take = 25,
  order = 'DepartureDateAscending',
  showCancelled = false,
) {
  var query = [
    showUpcoming != null ? `isUpcoming=${showUpcoming}` : null,
    showExpired != null ? `isExpired=${showExpired}` : null,
    showCancelled ? 'showCancelled=true' : null,
    `skip=${skip}`,
    `take=${take}`,
    `sort=${order}`,
  ].filter(Boolean).join('&')
  var response = await useFetchApi(`${onlineApiBase}Trips?${query}`)
  if (!response.ok) {
    throw response
  }
  var raw = parsePreserveJson<Record<string, unknown>>(await response.json())
  var trips = readPreserveArray<TripItem>(raw, 'trips', 'Trips').map((trip) => ({
    ...trip,
    policies: readPreserveArray<Policy>(trip as Record<string, unknown>, 'policies', 'Policies').map(normalizePolicyFields),
  }))
  var annualPolicies = readPreserveArray<Policy>(raw, 'annualPolicies', 'AnnualPolicies').map(normalizePolicy)
  return {
    trips: mapApiTripItems(trips),
    annualPolicies,
  }
}

function readPreserveArray<T>(raw: Record<string, unknown>, ...keys: string[]) {
  for (var key of keys) {
    var value = raw[key]
    if (Array.isArray(value)) {
      return value as T[]
    }
    if (value && typeof value === 'object' && Array.isArray((value as { $values?: unknown[] }).$values)) {
      return (value as { $values: T[] }).$values
    }
  }
  return [] as T[]
}

export async function getClientQuotesAsync(skip = 0, take = 25) {
  return fetchJson<GetQuotesResponse>(`${onlineApiBase}Quotes?skip=${skip}&take=${take}`)
}

export async function getClientPoliciesAsync(skip = 0, take = 25) {
  var response = await useFetchApi(`${onlineApiBase}Policies?skip=${skip}&take=${take}`)
  if (!response.ok) {
    throw response
  }
  var raw = parsePreserveJson<Record<string, unknown>>(await response.json())
  var policies = readPreserveArray<Policy>(raw, 'policies', 'Policies').map(normalizePolicy)
  return { policies } satisfies GetPoliciesResponse
}

export async function getClientPolicyDetailsAsync(policyId: string) {
  var response = await useFetchApi(`${onlineApiBase}Policies/${policyId}`)
  if (!response.ok) {
    throw response
  }
  var raw = await response.json() as Record<string, unknown>
  var policy = (raw.policy ?? raw.Policy) as Policy
  return { policy: normalizePolicy(policy) } satisfies GetPolicyResponse
}

export async function getPolicyDocumentsAsync(policyIds: string[], language: string) {
  return fetchJson<GetPolicyDocumentsResponse>(`${onlineApiBase}policies/documents`, {
    method: 'POST',
    body: JSON.stringify({ policiesIds: policyIds, language }),
  })
}

export async function getAgentAsync() {
  var response = await fetchJson<GetAgentResponse & {
    Email?: string;
    FirstName?: string;
    LastName?: string;
    PhoneExtension?: string;
  }>(`${onlineApiBase}agent`)
  var firstName = response.firstName ?? response.FirstName ?? ''
  var lastName = response.lastName ?? response.LastName ?? ''
  return {
    email: response.email ?? response.Email,
    firstName,
    lastName,
    phoneExtension: response.phoneExtension ?? response.PhoneExtension,
    fullName: response.fullName ?? `${firstName} ${lastName}`.trim(),
    hasAssignedAgent: !!(firstName || lastName || response.email || response.Email),
  } satisfies GetAgentResponse
}

export async function downloadPolicyDocumentAsync(policyId: string, documentName: string, language: string) {
  var encodedDocumentName = encodeURIComponent(documentName)
  var encodedLanguage = encodeURIComponent(language)
  var response = await useFetchApi(
    `${onlineApiBase}download-policy-document?policyId=${policyId}&documentName=${encodedDocumentName}&language=${encodedLanguage}`,
  )
  if (!response.ok) {
    throw response
  }
  return response.arrayBuffer()
}

export async function downloadQuoteDocumentsAsync(productId: string | null, language: string) {
  if (!productId) {
    return { locations: [] } as QuoteDocuments
  }
  return fetchJson<QuoteDocuments>(`${onlineApiBase}Download-quote-documents`, {
    method: 'POST',
    body: JSON.stringify({ productId, language }),
  })
}

export async function postClientMarketingPreferenceAsync(
  clientId: string | null,
  phone: boolean,
  email: boolean,
  mail: boolean,
) {
  if (!clientId) {
    return new Response(null, { status: 400 })
  }
  return useFetchApi(`${onlineApiBase}clients/marketingpreference`, {
    method: 'POST',
    body: JSON.stringify({ id: clientId, phone, email, mail }),
  })
}

export async function postClientPasswordResetAsync() {
  return useFetchApi(`${onlineApiBase}clients/password-reset`, {
    method: 'POST',
    body: '{}',
  })
}

export async function updateCreditCardInformationAsync(clientId: string | null, creditCard: DashboardCreditCard) {
  if (!clientId) {
    return new Response(null, { status: 400 })
  }
  return useFetchApi(`${onlineApiBase}clients/credit-card`, {
    method: 'POST',
    body: JSON.stringify({
      id: clientId,
      number: creditCard.number,
      fullName: creditCard.fullName,
      expiration: creditCard.expiration,
      ccv: creditCard.ccv,
    }),
  })
}

export async function changeDatesPolicyAsync(request: ChangePolicyDatesRequest) {
  return useFetchApi(`${onlineApiBase}ChangePolicyDates`, {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

/// Prices a prospective date change. The server resolves each traveler's own medical
/// questionnaire before pricing, which the client cannot do on its own.
export async function getChangeDatesPricingAsync(request: ChangePolicyDatesPricingRequest) {
  var response = await useFetchApi(`${onlineApiBase}ChangePolicyDatesPricing`, {
    method: 'POST',
    body: JSON.stringify(request),
  })
  if (!response.ok) {
    throw response
  }
  return await response.json() as ChangePolicyDatesPricingResponse
}

export async function postClientCallbackRequestAsync(callbackRequest: CallbackFormModel) {
  return useFetchApi(`${onlineApiBase}clients/callback`, {
    method: 'POST',
    body: JSON.stringify(callbackRequest),
  })
}

export async function getFeaturesAsync() {
  return fetchPublicJson<Record<string, boolean>>(`${onlineApiBase}features`)
}

export async function getPartnerPromoAsync(promoCode: string) {
  return fetchPublicJson(`${onlineApiBase}Partners/PromoCode/${encodeURIComponent(promoCode)}`)
}

export async function validateSignUpCodeAsync(email: string, code: string) {
  return fetchPublicJson(`${onlineApiBase}validate-code?email=${encodeURIComponent(email)}&code=${encodeURIComponent(code)}`)
}

export async function signUpAsync(payload: unknown) {
  return fetchPublicJson(`${onlineApiBase}sign-up`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function getPublicQuoteAsync(purchaseLinkId: string, clientId: string) {
  return fetchPublicJson(
    `${onlineApiBase}public-quote?PurchaseLinkId=${purchaseLinkId}&ClientId=${clientId}`,
  )
}

export async function buyQuoteAsync(payload: unknown) {
  return fetchPublicJson(`${onlineApiBase}Quotes/Buy`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function getProductTranslationsAsync(lang: string) {
  return fetchPublicJson(`/quotes/products/translations?lang=${encodeURIComponent(lang)}`)
}

export async function getDetailedPriceByDateAsync(productId: string, payload: unknown) {
  var response = await useFetchApi(`/quotes/${productId}/price-by-date/detailed`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  if (!response.ok) {
    throw response
  }
  return response.json()
}

function normalizePolicy(policy: Policy) {
  var normalized = normalizePolicyFields(policy)
  return {
    ...normalized,
    travelers: readPolicyArray<PolicyTraveler>(normalized as Record<string, unknown>, 'travelers', 'Travelers'),
  }
}

function readPolicyArray<T>(raw: Record<string, unknown>, ...keys: string[]) {
  for (var key of keys) {
    var value = raw[key]
    if (Array.isArray(value)) {
      return value as T[]
    }
    if (value && typeof value === 'object' && Array.isArray((value as { $values?: unknown[] }).$values)) {
      return (value as { $values: T[] }).$values
    }
  }
  return [] as T[]
}
